import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Cohort } from 'src/app/cohorts/services/cohort.service';
import { EntityType } from 'src/app/core/settings/settings.service';
import { Release } from 'src/app/releases/services/release.service';
import { Entity } from 'src/app/shared/entity-search/entity-search.service';
import { ModuleRef } from 'src/app/shared/module-search/module-search.component';
import { FirmwareUpdateApiService } from 'src/app/shared/services/firmware-update-api/firmware-update-api.service';
import { FirmwareService } from '../../../../modules/services/firmware/firmware.service';
import {
  AppVersionsQuery,
  DeviceVersionsQuery,
} from '../../../../shared/services/versions/versions.service';

@Component({
  selector: 'device-versions-query',
  templateUrl: './device-versions-query.component.html',
  styleUrls: ['./device-versions-query.component.scss'],
})
export class DeviceVersionsQueryComponent implements OnInit {
  @Input() total: number;
  @Input() isLoading?: boolean = false;
  @Output() query: EventEmitter<DeviceVersionsQuery> =
    new EventEmitter<DeviceVersionsQuery>();

  entities: Entity[] = [];
  modules: ModuleRef[] = [];
  cohorts: Cohort[] = [];
  releases: Release[] = [];
  selectedModules: ModuleRef[] = [];
  moduleStatus: string;
  selectedItemsByModule: { [key: string]: AppStatus[] } = {};

  statusOptions = ['Installed', 'Pending'];

  entitySearchTypes = [EntityType.company, EntityType.group, EntityType.device];

  constructor(
    private readonly modularUpdateService: FirmwareService,
    private readonly firmwareService: FirmwareUpdateApiService,
  ) {}

  protected get devices(): Entity[] {
    return this.entities.filter((e) => e.entityType === EntityType.device);
  }

  protected get groups(): Entity[] {
    return this.entities.filter((e) => e.entityType === EntityType.group);
  }

  protected get companies(): Entity[] {
    return this.entities.filter((e) => e.entityType === EntityType.company);
  }

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  downloadCsv() {
    const versionQuery = this.buildQuery(true);
    this.query.emit(versionQuery);
  }

  onCohortOrReleaseSelected(item: Release | Cohort) {
    if (this.isRelease(item)) {
      this.releases.push(item);
      return;
    }

    this.cohorts.push(item);
  }

  onEntitySelected(entity: any) {
    this.entities.push(entity);
  }

  onModuleSelected(module: any) {
    this.selectedModules.push(module);
    this.modules = this.filterModules(this.modules, module);
  }

  onReset() {
    this.entities = [];
    this.releases = [];
    this.cohorts = [];
    this.selectedModules = [];
    this.moduleStatus = null;
    this.loadData();
    this.onSearch();
  }

  onSearch() {
    const versionQuery = this.buildQuery();
    this.query.emit(versionQuery);
  }

  removeEntity(entity: Entity) {
    this.entities = this.entities.filter(
      (e) => !(e.path == entity.path && e.name == entity.name),
    );
  }

  removeModule(module: ModuleRef) {
    this.selectedModules = this.filterModules(this.selectedModules, module);
    this.modules.push(module);
  }

  removeRelease(release: Release) {
    const index = this.releases.indexOf(release);

    if (index > -1) {
      this.releases.splice(index, 1);
    }
  }

  removeCohort(cohort: Cohort) {
    const index = this.cohorts.indexOf(cohort);

    if (index > -1) {
      this.cohorts.splice(index, 1);
    }
  }

  async loadData() {
    this.modules = (await this.modularUpdateService.getModules()).map(
      (module) => {
        return {
          name: module.name,
          version: module.version,
        };
      },
    );
    this.modules = this.modules.concat(
      (await this.firmwareService.getFirmwareVersions()).map((module) => {
        return {
          name: module.name,
          version: module.version,
        };
      }),
    );
    this.modules = this.modules.filter(
      (module, index, self) =>
        index ===
        self.findIndex(
          (m) => m.name === module.name && m.version === module.version,
        ),
    );
  }

  onSelectionChanged(selectedItems: any[], module: any) {
    this.selectedItemsByModule[module.name] = selectedItems;
  }

  private buildQuery(useCsv = false): AppVersionsQuery {
    const serialNumbers = this.devices.map((e) => e.id);
    const groupIds = [
      ...this.groups.map((e) => e.id),
      ...this.companies.map((e) => e.rootGroupId),
    ];

    const modules = this.selectedModules.map((m) => {
      return {
        name: m.name,
        version: m.version,
        status: this.getModuleStatus(m),
      };
    });

    return {
      serialNumbers: serialNumbers,
      groupIds: groupIds,
      modules: modules,
      releasesAndCohorts: [...this.releases, ...this.cohorts].map(
        (item) => item.name,
      ),
      usePagination: true,
      returnCsv: useCsv,
      returnAllModules: modules.length === 0,
    };
  }

  private getModuleStatus(module: ModuleRef): string {
    if (!this.selectedItemsByModule[module.name]) {
      return null;
    }

    const installedSelected = this.selectedItemsByModule[module.name].find(
      (item) => item.name === 'Installed',
    )?.selected;
    const pendingSelected = this.selectedItemsByModule[module.name].find(
      (item) => item.name === 'Pending',
    )?.selected;
    if (
      (installedSelected && pendingSelected) ||
      (!installedSelected && !pendingSelected)
    ) {
      return null;
    }

    return installedSelected ? 'Installed' : 'Pending';
  }

  private filterModules(modules: ModuleRef[], module: ModuleRef): ModuleRef[] {
    return modules.filter(
      (m) => !(m.name === module.name && m.version === module.version),
    );
  }

  private isRelease(item: Release | Cohort): item is Release {
    return 'modules' in item;
  }
}

export interface AppStatus {
  name: string;
  selected: boolean;
}
